import {
	faChevronRight,
	faSort,
	faSortDown,
	faSortUp,
} from '@fortawesome/pro-solid-svg-icons';
import { computeEffectiveColumnFloor } from '@supply-mover-2-0/core';
import type { HTMLAttributes } from 'react';
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { usePagination } from '../../hooks/usePagination';
import { useListBuilderContextSafe } from '../../list-builder/components/core/ListBuilderProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { useScrollContext } from '../../services/ScrollContext';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Checkbox } from '../forms/Checkbox';
import { LoadingSpinner } from './LoadingSpinner';
import { TablePaginationControls } from './TablePaginationControls';

// Auto-sizing table components
export interface TableColumn {
	key: string;
	label: string | React.ReactNode;
	align?: 'left' | 'center' | 'right';
	// Fixed width (acts as both minWidth and maxWidth). Some list-builder
	// configs author strings here (Tailwind classes like 'w-16' / 'flex-1');
	// those are not usable as pixel widths, so the sizing math treats
	// string-width columns as flexible.
	width?: number | string;
	minWidth?: number;
	maxWidth?: number;
	// Relative weight used when distributing leftover horizontal space among
	// flexible columns. A larger weight absorbs proportionally more slack, so a
	// wide text column expands to fill available space instead of truncating
	// while compact columns leave whitespace (PD-14094). Defaults to an even
	// share when omitted, so tables that don't set it are unaffected.
	growWeight?: number;
	sortable?: boolean;
	sticky?: boolean; // Make this column sticky to the left when scrolling horizontally
	render?: (value: any, item: any, index: number) => React.ReactNode;
	// Per-row column span. Return N > 1 from this column to emit a single
	// <td colSpan={N}> and absorb the next N-1 columns' cells for this row.
	// Useful for drilldown/sub-header rows that don't map 1:1 to the
	// parent-row columns.
	colSpan?: (value: any, item: any, index: number) => number;
	// Optional cell renderer for the totals/footer row (see TableProps.footerRow).
	// Falls back to `render` (called with index -1), then to the raw value.
	renderFooter?: (value: unknown, item: unknown) => React.ReactNode;
}

interface MeasuredSizes {
	[columnKey: string]: number;
}

/**
 * Toggled per-row expansion (PD-15221). Distinct from `getRowExpansion`,
 * which renders an ALWAYS-VISIBLE callout banner above the row (used for
 * validation messages): `expandableRows` adds a leading caret column and a
 * user-toggled detail area BELOW the row.
 */
export interface ExpandableRowsConfig<T = unknown> {
	/** Content of the expanded detail area. Rendered only while open. */
	renderContent: (row: T, index: number) => React.ReactNode;
	/**
	 * Rows returning false get no caret and can't be expanded (e.g. a quote
	 * with zero lines). Default: every row is expandable.
	 */
	isRowExpandable?: (row: T, index: number) => boolean;
	/**
	 * When true, any number of rows can be open at once; when false
	 * (default), opening a row collapses the previously open one.
	 */
	allowMultiple?: boolean;
}

/**
 * Slide-down reveal for the expansion detail area. Mounts collapsed (0fr)
 * and transitions to 1fr on the next frame, so the row content slides open
 * and pushes the following rows down instead of popping in.
 *
 * Once the slide settles the clipping wrapper is REMOVED entirely: an
 * `overflow: hidden` ancestor becomes the scrollport for descendant
 * `position: sticky` elements, which silently defeats the viewport-pinned
 * expansion panel on horizontally scrolling tables. A timeout backs up
 * onTransitionEnd for environments where the transition never fires
 * (reduced motion, interrupted paint).
 */
const ExpansionReveal: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [open, setOpen] = useState(false);
	const [settled, setSettled] = useState(false);

	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			setOpen(true);
		});
		const fallback = setTimeout(() => {
			setSettled(true);
		}, 350);
		return (): void => {
			cancelAnimationFrame(frame);
			clearTimeout(fallback);
		};
	}, []);

	if (settled) {
		return <>{children}</>;
	}

	return (
		<div
			className="grid transition-[grid-template-rows] duration-200 ease-out"
			style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
			onTransitionEnd={() => {
				setSettled(true);
			}}
		>
			<div className="min-h-0 overflow-hidden">{children}</div>
		</div>
	);
};

export interface TableProps<T = any>
	extends Omit<HTMLAttributes<HTMLDivElement>, 'onScroll'> {
	columns: TableColumn[];
	data: T[];
	onSort?: (key: string, direction: 'asc' | 'desc') => void;
	// Flows into inline style.height, so CSS strings ('100%') are valid.
	height?: number | string;
	maxHeight?: number;
	/**
	 * Minimum width (px) for the inner `<table>` element. Use this
	 * instead of putting `min-w-[...]` on `className` — that puts the
	 * min-width on the OUTER wrapper, which makes the wrapper overflow
	 * its parent and creates a second, redundant horizontal scrollbar.
	 * `minWidth` here keeps the wrapper sized to its parent and only
	 * the table's internal scroll handles horizontal overflow.
	 */
	minWidth?: number;
	stickyHeader?: boolean;
	striped?: boolean;
	compact?: boolean;
	getRowClassName?: (row: T, index: number) => string;
	getRowExpansion?: (row: T, index: number) => React.ReactNode | null;
	/**
	 * Where `getRowExpansion` content is placed relative to its own row.
	 *
	 * Defaults to `'above'` because the shop/cart surfaces render cart
	 * VALIDATION messages through this hook (PD-12222) and read as a warning
	 * banner sitting on top of the offending line. A conventional disclosure
	 * expander — where the chevron opens detail for the row you clicked —
	 * wants `'below'`, which is what a caller should pass for anything that
	 * is detail rather than a warning about the row.
	 */
	expansionPosition?: 'above' | 'below';
	/**
	 * Who owns the VERTICAL scroll for the rows.
	 *
	 * Defaults to `'container'`: the wrapper carries `h-full`, which Blink
	 * resolves against the nearest ancestor with a definite height — in the CRM
	 * shell that is the app's content scroller — so a table taller than the
	 * viewport scrolls inside its own box. That is right for a screen whose table
	 * IS the screen, and it is what every existing caller gets.
	 *
	 * Pass `'page'` when the table is one block on a page that scrolls as a whole
	 * (a detail screen with a header, cards and a table under them). Without it
	 * the wrapper becomes a second scrollport sitting in the middle of the page:
	 * a wheel over the table scrolls the rows, and `overscroll-y-contain` then
	 * stops the gesture rather than letting it chain on to the page, so the page
	 * appears frozen while the cursor is over the table. In `'page'` mode the
	 * wrapper is left at its natural height and only scrolls horizontally, so
	 * vertical wheel input always reaches the page.
	 *
	 * `height` and `maxHeight` are ignored in `'page'` mode — capping the box
	 * while vertical overflow is hidden would silently clip rows.
	 */
	verticalScroll?: 'container' | 'page';
	/** Toggled caret expansion below each row — see ExpandableRowsConfig. */
	expandableRows?: ExpandableRowsConfig<T>;
	onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
	onRowClick?: (row: T, index: number) => void;
	selectedRows?: Set<string | number>;
	onSelectRow?: (rowId: string | number, selected: boolean) => void;
	onSelectAll?: (selected: boolean) => void;
	showCheckboxes?: boolean;
	loading?: boolean;
	emptyMessage?: string;
	/** Enable client-side pagination. Table slices data internally. */
	pagination?: {
		pageSize?: number; // default: 100
	};
	/**
	 * Server-driven pagination (PD-15826). `data` is ALREADY the page — the
	 * table renders it unsliced and drives the same pinned pager from the
	 * caller's state, so a report whose result set is six figures costs one
	 * page's worth of payload and DOM.
	 *
	 * Mutually exclusive with `pagination`; pass one or the other.
	 */
	serverPagination?: {
		/** 1-based page currently displayed. */
		currentPage: number;
		/** Rows per page — used to derive the page count and the row range. */
		pageSize: number;
		/** Rows matching the current query across every page. */
		totalItems: number;
		onPageChange: (page: number) => void;
		/** Greys out the controls while the next page is in flight. */
		loading?: boolean;
	};
	/** Enable built-in client-side sorting. When true, clicking sortable column headers
	 *  sorts data internally. When false/omitted, sorting is handled externally via onSort callback. */
	clientSort?: boolean;
	/**
	 * Server-driven sorting (PD-15826). The table renders the sort indicator
	 * from this state and reports the next (key, direction) pair; the caller
	 * re-queries. Use instead of `clientSort` when only one page is loaded —
	 * sorting a single page client-side would silently sort the wrong set.
	 */
	serverSort?: {
		sortKey: string | null;
		sortDirection: 'asc' | 'desc';
		onSortChange: (key: string, direction: 'asc' | 'desc') => void;
	};
	/**
	 * Optional totals/footer record pinned to the bottom of the table. Rendered
	 * as a sticky <tfoot> row inside the same column grid as body rows (same
	 * widths and alignment), excluded from sorting and pagination — the caller
	 * computes it over the FULL dataset, not the current page. Cells render via
	 * column.renderFooter when present, else column.render, else the raw value.
	 */
	footerRow?: T;
}

// Helper function to get scrollbar width (keep for width calculations)
const getScrollbarWidth = () => {
	if (typeof window === 'undefined') {
		return 0;
	}

	const outer = document.createElement('div');
	outer.style.visibility = 'hidden';
	outer.style.overflow = 'scroll';
	outer.style.width = '100px';
	outer.style.position = 'absolute';
	outer.style.top = '-9999px';
	document.body.appendChild(outer);

	const inner = document.createElement('div');
	inner.style.width = '100%';
	outer.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
	outer.parentNode?.removeChild(outer);

	return scrollbarWidth;
};

// Horizontal padding (px-3 = 12px each side) on a header/body cell, used to
// translate a measured label/content width into a full column width.
const CELL_HORIZONTAL_PADDING = 24;
// How many body rows to sample when measuring natural content width. Caps the
// cost of the hidden measurement table on long lists.
const MEASURE_ROW_SAMPLE = 40;
// Upper bound on how wide a single column's measured content may seed, so one
// freakishly long cell can't blow out the layout. Columns can still grow past
// this via the distributor's grow weights when there's room.
const MEASURE_CONTENT_CAP = 480;

const sizesEqual = (a: MeasuredSizes, b: MeasuredSizes): boolean => {
	const aKeys = Object.keys(a);
	if (aKeys.length !== Object.keys(b).length) {
		return false;
	}
	return aKeys.every((k) => a[k] === b[k]);
};

export function Table<T = any>({
	columns,
	data,
	onSort,
	className = '',
	height,
	maxHeight,
	minWidth,
	stickyHeader = true,
	striped = true,
	compact = false,
	getRowClassName,
	getRowExpansion,
	expansionPosition = 'above',
	verticalScroll = 'container',
	expandableRows,
	onScroll,
	onRowClick,
	selectedRows,
	onSelectRow,
	onSelectAll,
	showCheckboxes,
	loading = false,
	emptyMessage = 'No data available',
	pagination,
	serverPagination,
	clientSort = false,
	serverSort,
	footerRow,
	style,
	...props
}: TableProps<T>) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const listKey = useListBuilderContextSafe()?.listKey;

	// The table is a block on a page that scrolls as a whole, so it must not
	// become a scrollport of its own. `overflow-y-hidden` clips nothing here (the
	// box is at its natural height) but it does stop the horizontal scrollbar's
	// reserved strip from turning into a few pixels of vertical scroll range,
	// which is enough to swallow a wheel gesture.
	const scrollsWithPage = verticalScroll === 'page';
	const scrollportClasses = scrollsWithPage
		? 'overflow-x-auto overflow-y-hidden custom-scrollbar'
		: 'h-full overflow-auto overscroll-y-contain custom-scrollbar';
	// Same rule inside the paginated layout, where the scrollport is the middle
	// row of a flex column rather than the wrapper itself.
	const paginatedScrollportClasses = scrollsWithPage
		? 'overflow-x-auto overflow-y-hidden custom-scrollbar'
		: 'flex-1 min-h-0 overflow-auto overscroll-y-contain custom-scrollbar';

	// Toggled row expansion state. Keys mirror the React row keys (_id/id
	// fallback to index) so expansion survives re-renders; keys for rows that
	// leave the page simply go stale and are ignored.
	const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string | number>>(
		new Set(),
	);

	const getExpansionRowKey = useCallback(
		(item: T, index: number): string | number => {
			const rowRecord = item as Record<string, unknown>;
			return (rowRecord['_id'] ?? rowRecord['id'] ?? index) as string | number;
		},
		[],
	);

	const toggleRowExpansion = useCallback(
		(rowKey: string | number) => {
			setExpandedRowKeys((previous) => {
				const next = new Set(previous);
				if (next.has(rowKey)) {
					next.delete(rowKey);
				} else {
					if (expandableRows?.allowMultiple !== true) {
						next.clear();
					}
					next.add(rowKey);
				}
				return next;
			});
		},
		[expandableRows?.allowMultiple],
	);

	const [columnWidths, setColumnWidths] = useState<MeasuredSizes>({});
	// Content-aware widths per column key, measured from a hidden auto-layout
	// copy of the table (PD-14094). `measuredWidths` is the natural width the
	// column wants (max of header + body content); `measuredHeaders` is just the
	// header label width (the floor a column may shrink to in a too-narrow table,
	// so the column header never truncates). Lets a sparse column collapse toward
	// its label instead of reserving an over-large configured minWidth.
	const [measuredWidths, setMeasuredWidths] = useState<MeasuredSizes>({});
	const [measuredHeaders, setMeasuredHeaders] = useState<MeasuredSizes>({});
	const measureRef = useRef<HTMLTableElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0); // Start with 0 to prevent premature calculations
	const [hasInitializedWidths, setHasInitializedWidths] = useState(false);
	const [isResizing, setIsResizing] = useState<string | null>(null);
	const [manuallyResizedColumns, setManuallyResizedColumns] = useState<
		Set<string>
	>(new Set());
	const [hoveredRow, setHoveredRow] = useState<number | null>(null);

	// Memoize scrollbar width to avoid recalculating
	const scrollbarWidth = useMemo(() => {
		if (height && data.length > 0) {
			return getScrollbarWidth();
		}
		return 0;
	}, [height, data.length]);

	// Client-side sort state (only used when clientSort is enabled)
	const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
	const [internalSortDirection, setInternalSortDirection] = useState<
		'asc' | 'desc'
	>('asc');

	// Generic comparator for client-side sorting
	const sortedData = useMemo(() => {
		if (!clientSort || internalSortKey == null) {
			return data;
		}

		return [...data].sort((a, b) => {
			const aVal = (a as Record<string, unknown>)[internalSortKey];
			const bVal = (b as Record<string, unknown>)[internalSortKey];

			// Nulls go to the end regardless of direction
			if (aVal == null && bVal == null) return 0;
			if (aVal == null) return 1;
			if (bVal == null) return -1;

			let comparison = 0;
			if (typeof aVal === 'number' && typeof bVal === 'number') {
				comparison = aVal - bVal;
			} else if (typeof aVal === 'string' && typeof bVal === 'string') {
				comparison = aVal.localeCompare(bVal, undefined, {
					sensitivity: 'base',
				});
			} else {
				// Fallback: convert to string for comparison
				comparison = String(aVal).localeCompare(String(bVal), undefined, {
					sensitivity: 'base',
				});
			}

			return internalSortDirection === 'asc' ? comparison : -comparison;
		});
	}, [data, clientSort, internalSortKey, internalSortDirection]);

	// Pagination: slice the (optionally sorted) data
	const clientPaginationEnabled = pagination != null;
	const serverPaginationEnabled = serverPagination != null;
	const paginationEnabled = clientPaginationEnabled || serverPaginationEnabled;
	const pageSize = pagination?.pageSize ?? 100;

	const {
		currentPage: clientCurrentPage,
		totalPages: clientTotalPages,
		paginatedData,
		goToPage,
		startIndex: clientStartIndex,
		endIndex: clientEndIndex,
		totalItems: clientTotalItems,
	} = usePagination({
		data: sortedData,
		itemsPerPage: pageSize,
	});

	// In server mode `data` is already the page, so the row range is computed
	// from the caller's page state rather than from the array length.
	const serverRowRange = useMemo(() => {
		if (serverPagination == null) {
			return null;
		}

		const size = Math.max(1, serverPagination.pageSize);
		const total = Math.max(0, serverPagination.totalItems);
		const totalPages = Math.max(1, Math.ceil(total / size));
		const page = Math.min(
			Math.max(1, serverPagination.currentPage),
			totalPages,
		);
		const start = total > 0 ? (page - 1) * size + 1 : 0;

		return {
			currentPage: page,
			totalPages,
			startIndex: start,
			endIndex: Math.min(page * size, total),
			totalItems: total,
		};
	}, [serverPagination]);

	const currentPage = serverRowRange?.currentPage ?? clientCurrentPage;
	const totalPages = serverRowRange?.totalPages ?? clientTotalPages;
	const pageStartIndex = serverRowRange?.startIndex ?? clientStartIndex;
	const pageEndIndex = serverRowRange?.endIndex ?? clientEndIndex;
	const totalItems = serverRowRange?.totalItems ?? clientTotalItems;

	// The data to actually render in the table body. Server pagination has
	// already windowed it — slicing again would show a page of a page.
	const renderData = clientPaginationEnabled ? paginatedData : sortedData;

	// Handle column header click for sorting
	const handleSortClick = useCallback(
		(key: string) => {
			if (clientSort) {
				setInternalSortDirection((prevDir) => {
					if (internalSortKey === key) {
						return prevDir === 'asc' ? 'desc' : 'asc';
					}
					return 'desc';
				});
				setInternalSortKey(key);
			}

			if (serverSort != null) {
				// First click on a column sorts descending — the interesting end
				// of a report column (biggest value, most recent) is what someone
				// is looking for; clicking the active column flips it.
				const nextDirection =
					serverSort.sortKey === key && serverSort.sortDirection === 'desc'
						? 'asc'
						: 'desc';
				serverSort.onSortChange(key, nextDirection);
			}

			// Also call external onSort if provided (for analytics/tracking)
			if (onSort != null) {
				const newDirection =
					internalSortKey === key
						? internalSortDirection === 'asc'
							? 'desc'
							: 'asc'
						: 'desc';
				onSort(key, newDirection);
			}
		},
		[clientSort, serverSort, internalSortKey, internalSortDirection, onSort],
	);

	// Calculate sticky column positions
	const stickyColumnPositions = useMemo(() => {
		const positions: { [key: string]: number } = {};
		let leftOffset = 0;

		columns.forEach((column) => {
			if (column.sticky) {
				positions[column.key] = leftOffset;
				const numericWidth =
					typeof column.width === 'number' ? column.width : undefined;
				const width =
					columnWidths[column.key] || numericWidth || column.minWidth || 120;
				leftOffset += width;
			}
		});

		return positions;
	}, [columns, columnWidths]);

	const containerRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLTableElement>(null);

	// Calculate column widths based on container dimensions and constraints
	const calculateColumnWidths = useCallback(() => {
		// Content-first seed for a flexible column (PD-14094): the width it
		// naturally wants — the larger of its header label and its widest sampled
		// content (capped) — instead of an over-large configured minWidth. Falls
		// back to the configured minWidth until the measurement pass has run.
		const seedFloor = (col: TableColumn): number => {
			const natural = measuredWidths[col.key];
			if (natural == null) {
				return col.minWidth || 120;
			}
			return computeEffectiveColumnFloor({
				headerWidth: measuredHeaders[col.key] ?? 0,
				contentWidth: Math.min(natural, col.maxWidth ?? MEASURE_CONTENT_CAP),
				maxWidth: col.maxWidth,
			});
		};
		// The hard floor a flexible column may shrink to when the table is too
		// narrow to honor every seed — its header label width, so the column
		// header never truncates. Falls back to the configured minWidth.
		const hardFloor = (col: TableColumn): number => {
			const header = measuredHeaders[col.key];
			if (header == null) {
				return col.minWidth || 120;
			}
			return computeEffectiveColumnFloor({
				headerWidth: header,
				contentWidth: 0,
				maxWidth: col.maxWidth,
			});
		};

		setColumnWidths((prevWidths) => {
			const widths: MeasuredSizes = { ...prevWidths };

			// If any column has been manually resized, stop trying to fit table to container width
			const hasManualResizes = manuallyResizedColumns.size > 0;

			// Step 1: Identify column types and calculate initial requirements
			const fixedColumns: TableColumn[] = [];
			const flexibleColumns: TableColumn[] = [];
			const manuallyResizedList: TableColumn[] = [];
			let totalFixedWidth = 0;
			let totalManualWidth = 0;

			columns.forEach((col) => {
				const numericWidth =
					typeof col.width === 'number' ? col.width : undefined;
				if (manuallyResizedColumns.has(col.key)) {
					// Manually resized columns - preserve their current width
					manuallyResizedList.push(col);
					totalManualWidth +=
						widths[col.key] || numericWidth || col.minWidth || 120;
				} else if (numericWidth != null && numericWidth > 0) {
					// Fixed width columns
					fixedColumns.push(col);
					widths[col.key] = numericWidth;
					totalFixedWidth += numericWidth;
				} else {
					// Flexible columns (not manually resized)
					flexibleColumns.push(col);
					if (!widths[col.key]) {
						widths[col.key] = seedFloor(col); // content-first starting width
					}
				}
			});

			if (hasManualResizes) {
				// User has manually resized columns - don't constrain to container width
				flexibleColumns.forEach((col) => {
					if (!widths[col.key]) {
						widths[col.key] = seedFloor(col);
					}
				});
			} else {
				// No manual resizes - try to fit table to container width
				// PD-14094: cells are box-sizing:border-box, so each column width
				// ALREADY includes its px-3 padding (the content measurement
				// captures padding too), and the table is border-collapse, so there
				// is no inter-cell spacing. Subtracting per-cell padding + spacing
				// here made the distributed widths sum to ~200px LESS than the
				// container; with `min-w-full` the table then handed that gap to the
				// browser's content-blind table-fixed stretch — which truncated
				// wide columns while leaving whitespace. The only real overhead
				// OUTSIDE the `columns` widths is the leading checkbox column, the
				// table border, and the vertical scrollbar.
				const leadingColsWidth =
					(showCheckboxes === true ? 40 : 0) +
					(expandableRows != null ? 40 : 0);
				const borderWidth = 2;
				const buffer = 2;
				const availableWidth = Math.max(
					100,
					containerWidth -
						leadingColsWidth -
						borderWidth -
						scrollbarWidth -
						buffer,
				);
				const reservedWidth = totalFixedWidth + totalManualWidth;
				const remainingWidth = Math.max(0, availableWidth - reservedWidth);

				if (flexibleColumns.length > 0) {
					if (remainingWidth > 0) {
						// Distribute available width among flexible columns
						const totalFlexibleMinWidth = flexibleColumns.reduce(
							(sum, col) => sum + seedFloor(col),
							0,
						);

						if (remainingWidth >= totalFlexibleMinWidth) {
							// We have enough space - hand out the leftover space using each
							// column's grow weight (derived from its width preset) instead of
							// splitting it evenly. A wide text column (large preset) absorbs
							// proportionally more than a compact numeric column (small preset),
							// so it fills the available width instead of truncating while the
							// numeric column leaves whitespace (PD-14094). Any space a column
							// can't take because it hit its maxWidth is recycled to the columns
							// that can still grow, so the table fills the container.
							const colWidth = new Map<string, number>();
							flexibleColumns.forEach((col) => {
								colWidth.set(col.key, seedFloor(col));
							});

							let pool = remainingWidth - totalFlexibleMinWidth;
							let growers = flexibleColumns.filter(
								(col) =>
									col.maxWidth == null ||
									(colWidth.get(col.key) ?? 0) < col.maxWidth,
							);

							// Iterate so that overflow recycled from maxWidth-capped columns
							// gets re-offered to the remaining growers. Bounded by the number
							// of flexible columns (each pass caps at least one, or finishes).
							let guard = 0;
							while (
								pool > 1 &&
								growers.length > 0 &&
								guard <= flexibleColumns.length
							) {
								guard += 1;
								let totalWeight = growers.reduce(
									(sum, col) => sum + (col.growWeight ?? 1),
									0,
								);
								// If nothing wants to absorb space (every remaining column is a
								// compact, weight-0 column), fall back to an even split so the
								// table still fills its container instead of leaving a trailing
								// gap.
								const evenSplit = totalWeight <= 0;
								if (evenSplit) {
									totalWeight = growers.length;
								}

								const poolAtPassStart = pool;
								const stillGrowing: TableColumn[] = [];
								growers.forEach((col) => {
									const weight = evenSplit ? 1 : (col.growWeight ?? 1);
									const share = poolAtPassStart * (weight / totalWeight);
									const current = colWidth.get(col.key) ?? 0;
									const next = current + share;

									if (col.maxWidth != null && next >= col.maxWidth) {
										pool -= col.maxWidth - current;
										colWidth.set(col.key, col.maxWidth);
									} else {
										pool -= share;
										colWidth.set(col.key, next);
										stillGrowing.push(col);
									}
								});

								// No column could absorb anything this pass — stop to avoid spinning.
								if (
									stillGrowing.length === growers.length &&
									pool >= poolAtPassStart
								) {
									break;
								}
								growers = stillGrowing;
							}

							flexibleColumns.forEach((col) => {
								widths[col.key] = Math.round(
									colWidth.get(col.key) ?? seedFloor(col),
								);
							});
						} else {
							// Not enough space - shrink below default but respect explicit minWidth
							let distributedWidth = 0;
							const absoluteMinWidth = 40; // Absolute minimum to keep columns usable

							flexibleColumns.forEach((col, index) => {
								const isLastColumn = index === flexibleColumns.length - 1;

								if (isLastColumn) {
									// Give remaining width to last column
									const targetWidth = remainingWidth - distributedWidth;
									const effectiveMin = hardFloor(col);
									widths[col.key] = Math.max(
										effectiveMin,
										Math.round(targetWidth),
									);
								} else {
									// Distribute space equally among flexible columns
									const targetWidth = remainingWidth / flexibleColumns.length;
									const effectiveMin = hardFloor(col);
									const roundedWidth = Math.max(
										effectiveMin,
										Math.round(targetWidth),
									);
									widths[col.key] = roundedWidth;
									distributedWidth += roundedWidth;
								}
							});
						}
					} else {
						// No space left for flexible columns - respect explicit minWidth or use small default
						flexibleColumns.forEach((col) => {
							const effectiveMin = hardFloor(col);
							widths[col.key] = effectiveMin;
						});
					}

					// Final precision check - only reduce if we can respect minWidth constraints
					const totalCalculatedWidth = Object.values(widths).reduce(
						(sum, w) => sum + w,
						0,
					);
					const actualOverflow = totalCalculatedWidth - availableWidth;

					if (actualOverflow > 1 && flexibleColumns.length > 0) {
						// Allow 1px tolerance
						// Check if we can reduce overflow while respecting explicit minWidth
						const totalReducibleSpace = flexibleColumns.reduce((sum, col) => {
							const currentWidth = widths[col.key] || 0;
							const effectiveMin = hardFloor(col); // Only respect explicit minWidth
							return sum + Math.max(0, currentWidth - effectiveMin);
						}, 0);

						if (totalReducibleSpace >= actualOverflow) {
							// We can reduce the overflow while respecting constraints
							let remainingOverflow = actualOverflow;

							flexibleColumns.forEach((col, index) => {
								if (remainingOverflow <= 0) {
									return;
								}

								const isLastColumn = index === flexibleColumns.length - 1;
								const currentWidth = widths[col.key] || 0;
								const effectiveMin = hardFloor(col);
								const maxReduction = currentWidth - effectiveMin;

								if (maxReduction > 0) {
									if (isLastColumn) {
										// Last column takes all remaining overflow (up to its limit)
										const reductionAmount = Math.min(
											remainingOverflow,
											maxReduction,
										);
										widths[col.key] = Math.round(
											currentWidth - reductionAmount,
										);
									} else {
										// Reduce proportionally
										const reductionAmount = Math.min(
											Math.ceil(
												remainingOverflow / (flexibleColumns.length - index),
											),
											maxReduction,
										);

										if (reductionAmount > 0) {
											widths[col.key] = Math.round(
												currentWidth - reductionAmount,
											);
											remainingOverflow -= reductionAmount;
										}
									}
								}
							});
						}
					}
				}
			}

			return widths;
		});
	}, [
		columns,
		manuallyResizedColumns,
		containerWidth,
		scrollbarWidth,
		measuredWidths,
		measuredHeaders,
		showCheckboxes,
		expandableRows,
	]);

	// Use ResizeObserver for container width changes (better than onLayout)
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const resizeObserver = new ResizeObserver((entries) => {
			const firstEntry = entries[0];
			if (firstEntry == null) {
				return;
			}
			const { width, height: observedHeight } = firstEntry.contentRect;

			if (width > 0 && Math.abs(width - containerWidth) > 1) {
				// Only update if significant change
				setContainerWidth(width);
			}
		});

		resizeObserver.observe(containerRef.current);
		return () => {
			resizeObserver.disconnect();
		};
	}, [containerWidth]);

	// Measure natural content + header-label widths from the hidden auto-layout
	// table (PD-14094). Runs after layout (the auto table has sized its columns
	// to content) and before paint; the visible table then seeds its widths from
	// these measurements so sparse columns collapse toward their label and the
	// freed space flows to the wide column. Guarded by sizesEqual so feeding the
	// numbers back into the visible table doesn't loop.
	useLayoutEffect(() => {
		const table = measureRef.current;
		if (table == null) {
			return;
		}

		const naturals: MeasuredSizes = {};
		const headers: MeasuredSizes = {};

		table
			.querySelectorAll<HTMLTableCellElement>('th[data-measure-col]')
			.forEach((th) => {
				const key = th.getAttribute('data-measure-col');
				if (key == null) {
					return;
				}
				naturals[key] = Math.ceil(th.getBoundingClientRect().width);
				const label = th.querySelector<HTMLElement>('[data-measure-label]');
				headers[key] =
					label != null
						? Math.ceil(label.getBoundingClientRect().width) +
							CELL_HORIZONTAL_PADDING
						: naturals[key];
			});

		setMeasuredWidths((prev) => (sizesEqual(prev, naturals) ? prev : naturals));
		setMeasuredHeaders((prev) => (sizesEqual(prev, headers) ? prev : headers));
		// renderData is referenced inside via the rendered measurement table, so
		// re-measure when the sampled rows or the column set change.
	}, [columns, renderData, scrollbarWidth]);

	// Determine if we should show content or wait for measurement
	const shouldShowContent = containerWidth > 0;

	// Calculate column widths on initial load and container width changes
	// Only calculate when we're actually going to show content
	useEffect(() => {
		if (shouldShowContent) {
			// Only recalculate if not currently resizing
			if (!isResizing) {
				calculateColumnWidths();
				setHasInitializedWidths(true);
			}
		}
	}, [
		shouldShowContent,
		containerWidth,
		columns,
		isResizing,
		scrollbarWidth,
		calculateColumnWidths,
	]);

	// Reset manual resize tracking when columns change
	useEffect(() => {
		if (hasInitializedWidths && shouldShowContent) {
			// Check if columns have changed significantly (different keys)
			const currentKeys = new Set(columns.map((col) => col.key));
			const existingKeys = new Set(Object.keys(columnWidths));

			const hasNewColumns = columns.some((col) => !existingKeys.has(col.key));
			const hasRemovedColumns = [...existingKeys].some(
				(key) => !currentKeys.has(key),
			);

			if (hasNewColumns || hasRemovedColumns) {
				// Reset manual resize tracking for removed columns
				setManuallyResizedColumns((prev) => {
					const newSet = new Set<string>();
					for (const key of prev) {
						if (currentKeys.has(key)) {
							newSet.add(key);
						}
					}
					return newSet;
				});

				// Add widths for new columns
				const newWidths = { ...columnWidths };
				columns.forEach((col) => {
					if (!newWidths[col.key]) {
						const numericWidth =
							typeof col.width === 'number' ? col.width : undefined;
						newWidths[col.key] = numericWidth || col.minWidth || 120;
					}
				});

				// Remove widths for removed columns
				Object.keys(newWidths).forEach((key) => {
					if (!currentKeys.has(key)) {
						delete newWidths[key];
					}
				});

				setColumnWidths(newWidths);
			}
		}
	}, [columns, hasInitializedWidths, columnWidths]);

	// Render content for a cell
	const renderCellContent = (column: TableColumn, item: T, index: number) => {
		const value = item[column.key as keyof T];

		if (column.render) {
			return column.render(value, item, index);
		}

		if (typeof value === 'string' || typeof value === 'number') {
			return <span className="block truncate">{value}</span>;
		}

		return null;
	};

	const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
		switch (align) {
			case 'center':
				return 'text-center';
			case 'right':
				// Right alignment means "this is a number" here; the guide forbids
				// right-aligning a text column. Tabular figures are what make a
				// column of numbers comparable without reading each one, so the two
				// belong together rather than being remembered separately.
				return 'text-right tabular-nums';
			default:
				return 'text-left';
		}
	};

	// Enhanced column resizing with native mouse events
	const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
		e.preventDefault();
		e.stopPropagation();

		const startX = e.clientX;
		const startWidth = columnWidths[columnKey] || 120;
		setIsResizing(columnKey);

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const newWidth = startWidth + deltaX;

			// Find the column configuration
			const column = columns.find((col) => col.key === columnKey);
			if (!column) {
				return;
			}

			// Apply constraints using user-defined minimum
			const numericWidth =
				typeof column.width === 'number' ? column.width : undefined;
			const effectiveMinWidth = numericWidth || column.minWidth || 60;
			const maxWidth = numericWidth || column.maxWidth;

			let constrainedWidth = Math.max(newWidth, effectiveMinWidth);
			if (maxWidth) {
				constrainedWidth = Math.min(constrainedWidth, maxWidth);
			}

			setColumnWidths((prev) => ({
				...prev,
				[columnKey]: constrainedWidth,
			}));
		};

		const handleMouseUp = () => {
			setIsResizing(null);
			setManuallyResizedColumns((prev) => new Set(prev).add(columnKey));
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	// Scroll context integration (simplified)
	const scrollContext = useScrollContext();
	const [scrollY, setScrollY] = useState(0);

	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const currentScrollY = event.currentTarget.scrollTop;
			setScrollY(currentScrollY);

			// Notify scroll context if available
			if (scrollContext) {
				// Note: This is a simplified integration - full context integration may need adjustment
			}

			// Call original onScroll if provided
			if (onScroll) {
				onScroll(event);
			}
		},
		[onScroll, scrollContext],
	);

	// Render sort indicator icon for clientSort / serverSort modes
	const renderSortIcon = (columnKey: string) => {
		const sortModeActive = clientSort || serverSort != null;
		if (
			!sortModeActive ||
			!columns.find((c) => c.key === columnKey)?.sortable
		) {
			return null;
		}

		const activeKey = serverSort != null ? serverSort.sortKey : internalSortKey;
		const activeDirection =
			serverSort != null ? serverSort.sortDirection : internalSortDirection;
		const isActive = activeKey === columnKey;
		const icon = isActive
			? activeDirection === 'asc'
				? faSortUp
				: faSortDown
			: faSort;

		return (
			<Icon
				icon={icon}
				size={12}
				color={
					isActive ? colors.values.primaryDark : colors.values.iconSecondary
				}
				style={{ marginLeft: 4, flexShrink: 0 }}
			/>
		);
	};

	// Render header cell with resizing handle
	const renderHeaderCell = (column: TableColumn, index: number) => {
		const width = columnWidths[column.key] || column.minWidth || 120;
		const isLastColumn = index === columns.length - 1;
		const isSticky = column.sticky;
		const stickyLeft = isSticky ? stickyColumnPositions[column.key] : undefined;

		// Determine the sort handler: use internal handleSortClick when either
		// sort mode is on (it owns the direction toggle), otherwise fall back to
		// external onSort
		const sortHandler =
			clientSort || serverSort != null
				? handleSortClick
				: onSort != null
					? (key: string) => {
							onSort(key, 'asc');
						}
					: undefined;

		return (
			<th
				key={column.key}
				className={cn(
					colors.classes.tableHeader,
					colors.classes.tableHeaderCell,
					colors.classes.borderDefault,
					'border-b px-control py-hair text-label uppercase tracking-wide relative select-none',
					getAlignmentClass(column.align),
					isSticky && 'sticky z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
				)}
				style={{
					width,
					minWidth: width,
					...(isSticky && { left: stickyLeft }),
				}}
				data-testid={listKey != null ? `${listKey}-column-header` : undefined}
			>
				{React.isValidElement(column.label) ? (
					column.sortable ? (
						<button
							onClick={() => {
								sortHandler?.(column.key);
							}}
							className={cn(
								// `uppercase` is restated here rather than inherited from
								// the th: the UA stylesheet sets `text-transform: none` on
								// `button` as a direct rule, so inheritance loses. Without
								// it the sortable columns render in sentence case and the
								// two non-sortable ones in caps, which reads as a bug.
								'w-full h-full flex items-center whitespace-nowrap uppercase',
								column.align === 'center' && 'justify-center',
								column.align === 'right' && 'justify-end',
								column.align === 'left' && 'justify-start',
								!column.align && 'justify-start',
								'hover:opacity-80 cursor-pointer',
								getAlignmentClass(column.align),
							)}
						>
							{column.label}
							{renderSortIcon(column.key)}
						</button>
					) : (
						<div
							className={cn(
								'w-full h-full flex items-center whitespace-nowrap',
								column.align === 'center' && 'justify-center',
								column.align === 'right' && 'justify-end',
								column.align === 'left' && 'justify-start',
								!column.align && 'justify-start',
							)}
						>
							{column.label}
						</div>
					)
				) : (
					<button
						onClick={() => {
							if (column.sortable) {
								sortHandler?.(column.key);
							}
						}}
						disabled={!column.sortable}
						className={cn(
							// See above: a button does not inherit the th's transform.
							'w-full h-full flex items-center uppercase',
							getAlignmentClass(column.align),
							column.align === 'center' && 'justify-center',
							column.align === 'right' && 'justify-end',
							column.sortable && 'hover:opacity-80 cursor-pointer',
						)}
					>
						<span className="block truncate">{column.label}</span>
						{renderSortIcon(column.key)}
					</button>
				)}

				{/* Column resize handle */}
				{!isLastColumn && (
					<div
						className={cn(
							'absolute top-0 right-0 w-2 h-full cursor-col-resize bg-transparent opacity-0 hover:opacity-100 transition-opacity',
							`hover:${colors.classes.bgPrimary}/20`,
						)}
						onMouseDown={(e) => {
							handleMouseDown(e, column.key);
						}}
					>
						<div
							className={cn(
								'w-px h-full mx-auto',
								colors.classes.borderDefault,
							)}
						/>
					</div>
				)}
			</th>
		);
	};

	// Total number of rendered columns including the leading caret/checkbox
	// cells — every full-width row (banner expansion, toggled expansion,
	// loading, empty) must span this.
	const totalColumnCount =
		columns.length +
		(showCheckboxes === true ? 1 : 0) +
		(expandableRows != null ? 1 : 0);

	// Render table row with expansion support
	const renderRow = (item: T, index: number) => {
		const expansion = getRowExpansion?.(item, index);
		// `getRowExpansion` may legitimately return `null`, `undefined` or `false`
		// to mean "this row has nothing to expand".
		const hasExpansion = expansion != null && expansion !== false;
		const rowClassName = getRowClassName?.(item, index) || '';
		const expansionRowKey = getExpansionRowKey(item, index);
		const rowIsExpandable =
			expandableRows != null &&
			(expandableRows.isRowExpandable?.(item, index) ?? true);
		const isExpanded = rowIsExpandable && expandedRowKeys.has(expansionRowKey);

		// Build base classes - don't apply stripe if there's a custom background
		const hasCustomBackground = rowClassName.includes('bg-');
		const isHovered = onRowClick != null && hoveredRow === index;
		const baseClasses = cn(
			index < renderData.length - 1 &&
				!hasExpansion &&
				!isExpanded &&
				cn('border-b', colors.classes.borderDefault),
			// Hover color is intentionally darker than the stripe color so the
			// hover state is visible on both striped and unstriped rows. We
			// also suppress the stripe class on the hovered row below because
			// the stripe's :nth-child(even) selector is more specific than a
			// plain bg utility and would otherwise win the cascade.
			isHovered && 'bg-control',
		);

		const isExpansionBelow = expansionPosition === 'below';

		// Rendered into the fragment either before or after the data row. The
		// vertical padding follows the position so the panel always hugs the row
		// it belongs to rather than the neighbouring one.
		const expansionRow = !hasExpansion ? null : (
			<tr className={cn(rowClassName, 'border-b-0')}>
				<td
					colSpan={totalColumnCount}
					className={cn(
						'px-control',
						isExpansionBelow ? 'pt-0 pb-control' : 'pb-0 pt-control',
					)}
				>
					<div
						className={cn(
							'rounded-lg p-4',
							isExpansionBelow ? 'mt-2' : 'mb-2',
							rowClassName.includes('bg-red')
								? colors.classes.calloutDanger
								: rowClassName.includes('bg-yellow')
									? colors.classes.calloutWarning
									: 'bg-control',
						)}
					>
						{expansion}
					</div>
				</td>
			</tr>
		);

		return (
			<React.Fragment key={(item as any)._id || (item as any).id || index}>
				{isExpansionBelow ? null : expansionRow}

				<tr
					className={cn(
						baseClasses,
						rowClassName,
						'transition-colors duration-150',
						striped &&
							!hasCustomBackground &&
							!isHovered &&
							!isExpanded &&
							colors.classes.tableRowStripe,
						// Persistent tint while expanded (same tone as hover): with
						// several rows open, the highlighted row + its recessed panel
						// read as one unit instead of interleaved table rows.
						isExpanded && !hasCustomBackground && 'bg-control',
					)}
					onMouseEnter={() => onRowClick && setHoveredRow(index)}
					onMouseLeave={() => onRowClick && setHoveredRow(null)}
					onClick={(e) => {
						if (onRowClick == null) return;
						// Don't fire row click when clicking interactive elements (buttons, links, inputs)
						const target = e.target as HTMLElement;
						if (
							target.closest('button, a, input, select, [role="button"]') !=
							null
						)
							return;
						onRowClick(item, index);
					}}
					style={{ cursor: onRowClick ? 'pointer' : 'default' }}
					data-testid={listKey != null ? `${listKey}-list-row` : undefined}
					// The row's own id, so a host can find and scroll to a specific
					// row after it changes — e.g. the cart bringing a line just added
					// from the suggestions strip into view (PD-13666).
					data-row-id={
						(item as { _id?: unknown; id?: unknown })._id != null
							? String((item as { _id?: unknown })._id)
							: (item as { id?: unknown }).id != null
								? String((item as { id?: unknown }).id)
								: undefined
					}
				>
					{expandableRows != null && (
						<td
							className={cn(
								'py-hair px-control text-center',
								colors.classes.tableCell,
							)}
							style={{ width: 40, minWidth: 40 }}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							{rowIsExpandable && (
								<button
									type="button"
									aria-expanded={isExpanded}
									aria-label={t('Toggle row details')}
									onClick={() => {
										toggleRowExpansion(expansionRowKey);
									}}
									className={cn(
										'inline-flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-control',
										colors.classes.textSecondary,
									)}
									data-testid={
										listKey != null ? `${listKey}-row-expand-toggle` : undefined
									}
								>
									<Icon
										icon={faChevronRight}
										className={cn(
											'text-xs transition-transform duration-200',
											isExpanded && 'rotate-90',
										)}
									/>
								</button>
							)}
						</td>
					)}
					{showCheckboxes === true &&
						(() => {
							// Prefer `id`, but fall back to `_id` for entities served straight
							// from TypeORM raw results (e.g. ItemsService). Matches the
							// extraction used by ListView's handleSelectAll so row selection
							// stays consistent regardless of which field the source exposes.
							const rowRecord = item as Record<string, unknown>;
							const rowId = (rowRecord['id'] ?? rowRecord['_id']) as
								| string
								| number;
							return (
								<td
									className={cn(
										'py-hair px-control text-center',
										colors.classes.tableCell,
									)}
									style={{ width: 40, minWidth: 40 }}
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<Checkbox
										checked={selectedRows != null && selectedRows.has(rowId)}
										onChange={(checked) => onSelectRow?.(rowId, checked)}
									/>
								</td>
							);
						})()}
					{(() => {
						// Track how many subsequent columns have been absorbed by a
						// prior column's colSpan so we can skip rendering their <td>.
						let skipRemaining = 0;
						return columns.map((column, colIndex) => {
							if (skipRemaining > 0) {
								skipRemaining -= 1;
								return null;
							}
							const value = (item as Record<string, unknown>)[column.key];
							const span = Math.max(
								1,
								Math.min(
									column.colSpan?.(value, item, index) ?? 1,
									columns.length - colIndex,
								),
							);
							if (span > 1) {
								skipRemaining = span - 1;
							}
							const width = columnWidths[column.key] || column.minWidth || 120;
							const isSticky = column.sticky;
							const stickyLeft = isSticky
								? stickyColumnPositions[column.key]
								: undefined;

							return (
								<td
									key={column.key}
									colSpan={span > 1 ? span : undefined}
									className={cn(
										'py-hair px-control overflow-hidden',
										colors.classes.tableCell,
										getAlignmentClass(column.align),
										isSticky &&
											cn(
												'sticky z-10',
												colors.classes.bgPrimary,
												'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
											),
									)}
									style={{
										width: span > 1 ? undefined : width,
										minWidth: span > 1 ? undefined : width,
										// PD-16184 — the cell keeps the width the table worked out
										// for it. Without a ceiling an unbreakable line (an address
										// with `truncate`, which is nowrap by definition) pushed the
										// cell out to its full text width, the row past the table's
										// edge, and a horizontal scrollbar under a list whose columns
										// had been sized to fit. The text now truncates at the width
										// the column was given, as its `truncate` class intends.
										maxWidth: span > 1 ? undefined : width,
										...(isSticky && { left: stickyLeft }),
									}}
								>
									{renderCellContent(column, item, index)}
								</td>
							);
						});
					})()}
				</tr>

				{isExpansionBelow ? expansionRow : null}

				{/* Toggled row expansion - detail area below the row.
				    isExpanded already implies expandableRows is set. */}
				{isExpanded && (
					<tr
						className={cn(
							index < renderData.length - 1 &&
								cn('border-b', colors.classes.borderDefault),
						)}
						data-testid={
							listKey != null ? `${listKey}-row-expansion` : undefined
						}
					>
						{/* The td carries the recessed (secondary) background across the
						    table's full scrollable width, so the nested content reads as
						    a level below the table surface even when scrolled. */}
						<td
							colSpan={totalColumnCount}
							className={cn('p-0', colors.classes.bgSecondary)}
						>
							<ExpansionReveal>
								{/* Pinned to the scroller's visible area: the td spans the
								    table's full scrollable width, so on a horizontally
								    scrolling table an unpinned panel would extend past the
								    viewport and its right-side columns would be reachable
								    only by scrolling the PARENT. sticky-left + the measured
								    container width keep the whole panel in view. The extra
								    left padding indents the nested card under the caret
								    column, tree-style. */}
								<div
									className="pl-10 pr-control pt-2 pb-control sticky left-0"
									style={{
										width: containerWidth > 0 ? containerWidth : undefined,
									}}
								>
									{expandableRows.renderContent(item, index)}
								</div>
							</ExpansionReveal>
						</td>
					</tr>
				)}
			</React.Fragment>
		);
	};

	// Hidden auto-layout twin of the table (PD-14094). Renders the real header
	// labels + a sample of real cell content with `table-auto` so the browser
	// sizes each column to its natural content; the useLayoutEffect above reads
	// those widths. It's visually hidden and absolutely positioned so it never
	// affects layout, and renders independently of `shouldShowContent` so the
	// measurement is ready by the time the visible table first paints.
	const measureTableElement =
		columns.length > 0 && renderData.length > 0 ? (
			<table
				ref={measureRef}
				aria-hidden="true"
				className="table-auto text-body"
				style={{
					position: 'absolute',
					top: 0,
					left: -99999,
					visibility: 'hidden',
					pointerEvents: 'none',
					zIndex: -1,
				}}
			>
				<thead>
					<tr>
						{columns.map((column) => (
							<th
								key={column.key}
								data-measure-col={column.key}
								className="px-control py-hair whitespace-nowrap text-left"
								style={{ maxWidth: column.maxWidth ?? MEASURE_CONTENT_CAP }}
							>
								{/* Mirror the real header cell's typography — it renders
								    text-label uppercase tracking-wide, all of which widen
								    the text beyond this table's default. Measuring in the
								    default face under-measured labels by ~15%, which
								    surfaced as clipped right-edge columns in any exactly
								    filled table (PD-15221's expansion panel). */}
								<span
									data-measure-label
									className="inline-flex items-center whitespace-nowrap text-label uppercase tracking-wide"
								>
									{column.label}
									{/* Reserve the sort caret's width (icon + margin) so a
									    sortable column's floor covers what actually renders. */}
									{column.sortable === true && (
										<span style={{ width: 16, flexShrink: 0 }} />
									)}
								</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{renderData.slice(0, MEASURE_ROW_SAMPLE).map((item, index) => (
						<tr key={index}>
							{columns.map((column) => (
								<td
									key={column.key}
									className="px-control py-hair whitespace-nowrap"
									style={{ maxWidth: column.maxWidth ?? MEASURE_CONTENT_CAP }}
								>
									{renderCellContent(column, item, index)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		) : null;

	// The table element, shared between paginated and non-paginated layouts
	const tableElement = shouldShowContent ? (
		<table
			ref={tableRef}
			className="border-collapse table-fixed text-body min-w-full"
			style={minWidth != null ? { minWidth } : undefined}
			data-testid={listKey != null ? `${listKey}-list-table` : undefined}
		>
			<thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
				<tr style={{ minHeight: '36px' }}>
					{expandableRows != null && (
						<th
							className={cn(
								colors.classes.tableHeader,
								colors.classes.tableHeaderCell,
								colors.classes.borderDefault,
								'border-b px-control py-hair',
							)}
							style={{ width: 40, minWidth: 40 }}
							aria-label={t('Row details')}
						/>
					)}
					{showCheckboxes === true && (
						<th
							className={cn(
								colors.classes.tableHeader,
								colors.classes.tableHeaderCell,
								colors.classes.borderDefault,
								'border-b px-control py-hair text-center',
							)}
							style={{ width: 40, minWidth: 40 }}
						>
							<Checkbox
								checked={
									data.length > 0 &&
									selectedRows != null &&
									selectedRows.size > 0 &&
									data.every((row) => {
										const rowRecord = row as Record<string, unknown>;
										const rowId = (rowRecord['id'] ?? rowRecord['_id']) as
											| string
											| number;
										return selectedRows.has(rowId);
									})
								}
								indeterminate={
									selectedRows != null &&
									selectedRows.size > 0 &&
									!data.every((row) => {
										const rowId = (row as Record<string, unknown>)['id'] as
											| string
											| number;
										return selectedRows.has(rowId);
									})
								}
								onChange={(checked) => onSelectAll?.(checked)}
							/>
						</th>
					)}
					{columns.map(renderHeaderCell)}
				</tr>
			</thead>
			<tbody>
				{loading ? (
					<tr>
						<td colSpan={totalColumnCount} className="text-center py-8">
							<div
								className="inline-flex items-center"
								data-testid={
									listKey != null ? `${listKey}-table-loading` : undefined
								}
							>
								<LoadingSpinner size="sm" color="gray" className="mr-3" />
								<span className={colors.classes.textSecondary}>Loading...</span>
							</div>
						</td>
					</tr>
				) : data.length === 0 ? (
					<tr>
						<td
							colSpan={totalColumnCount}
							className={cn('text-center py-12', colors.classes.textSecondary)}
							data-testid={
								listKey != null ? `${listKey}-table-empty` : undefined
							}
						>
							<div className="flex items-center justify-center">
								<span className="text-sm">{emptyMessage}</span>
							</div>
						</td>
					</tr>
				) : (
					renderData.map(renderRow)
				)}
			</tbody>
			{footerRow != null && !loading && data.length > 0 && (
				<tfoot className="sticky bottom-0 z-10">
					<tr>
						{expandableRows != null && (
							<td
								className={cn(
									colors.classes.tableHeader,
									colors.classes.borderDefault,
									'border-t px-control py-hair',
								)}
								style={{ width: 40, minWidth: 40 }}
							/>
						)}
						{showCheckboxes === true && (
							<td
								className={cn(
									colors.classes.tableHeader,
									colors.classes.borderDefault,
									'border-t px-control py-hair',
								)}
								style={{ width: 40, minWidth: 40 }}
							/>
						)}
						{columns.map((column) => {
							const width = columnWidths[column.key] ?? column.minWidth ?? 120;
							const value = (footerRow as Record<string, unknown>)[column.key];
							const isSticky = column.sticky === true;
							const stickyLeft = isSticky
								? stickyColumnPositions[column.key]
								: undefined;

							return (
								<td
									key={column.key}
									className={cn(
										'py-hair px-control overflow-hidden border-t font-semibold',
										colors.classes.tableHeader,
										colors.classes.tableCell,
										colors.classes.borderDefault,
										getAlignmentClass(column.align),
										isSticky && 'sticky z-10',
									)}
									style={{
										width,
										minWidth: width,
										...(isSticky && { left: stickyLeft }),
									}}
								>
									{column.renderFooter != null ? (
										column.renderFooter(value, footerRow)
									) : column.render != null ? (
										column.render(value, footerRow, -1)
									) : typeof value === 'string' || typeof value === 'number' ? (
										<span className="block truncate">{value}</span>
									) : null}
								</td>
							);
						})}
					</tr>
				</tfoot>
			)}
		</table>
	) : (
		// Pre-measurement placeholder. It fills the wrapper in the default mode,
		// but a page-scrolling table has no height of its own to fill — `h-full`
		// there would resolve against the page scroller and flash a
		// viewport-tall empty box before the first measurement lands.
		<div
			className={scrollsWithPage ? 'w-full' : 'h-full w-full'}
			style={{ overflow: 'hidden' }}
		/>
	);

	// When pagination is enabled, wrap in a flex layout with controls pinned at bottom
	if (paginationEnabled) {
		return (
			<div
				className={cn(
					'border rounded-t-lg flex flex-col',
					scrollsWithPage ? '' : 'h-full',
					colors.classes.borderDefault,
					colors.classes.bgPrimary,
					className,
				)}
				style={(() => {
					const computedHeight =
						!scrollsWithPage && height ? height : undefined;
					const computedMaxHeight =
						shouldShowContent && !scrollsWithPage
							? !height && maxHeight
								? maxHeight
								: undefined
							: undefined;

					return {
						height: computedHeight,
						maxHeight: computedMaxHeight,
						position: 'relative' as const,
						...style,
					};
				})()}
				{...props}
			>
				<div
					ref={containerRef}
					className={paginatedScrollportClasses}
					onScroll={shouldShowContent ? handleScroll : undefined}
				>
					{measureTableElement}
					{tableElement}
				</div>
				<div className={cn('border-t', colors.classes.borderDefault)}>
					<TablePaginationControls
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={serverPagination?.onPageChange ?? goToPage}
						disabled={serverPagination?.loading === true}
						startIndex={pageStartIndex}
						endIndex={pageEndIndex}
						totalItems={totalItems}
						testIdPrefix={listKey ?? undefined}
					/>
				</div>
			</div>
		);
	}

	// Non-paginated: original single-div layout
	return (
		<div
			ref={containerRef}
			className={cn(
				'border rounded-t-lg',
				scrollportClasses,
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				className,
			)}
			style={(() => {
				const computedHeight = !scrollsWithPage && height ? height : undefined;
				const computedMaxHeight =
					shouldShowContent && !scrollsWithPage
						? !height && maxHeight
							? maxHeight
							: undefined
						: undefined;

				return {
					height: computedHeight,
					maxHeight: computedMaxHeight,
					position: 'relative' as const,
					...style,
				};
			})()}
			onScroll={shouldShowContent ? handleScroll : undefined}
			{...props}
		>
			{measureTableElement}
			{tableElement}
		</div>
	);
}
